/**
 * Jaipur Handloom AE8 engagement adapter
 * File: jh-70-cart-sync.js
 * Owns: cart/recent-product intent context used by save-cart and exit messaging.
 * Depends on: jh-00-core.js; listens to cart events from jh-90-cart.js.
 * It never renders, mutates or intercepts the cart itself.
 */
(function() {
  'use strict';

  var JH = window.JH = window.JH || {};
  var U = JH.Utils;

  function qs(selector, root) {
    return U ? U.qs(selector, root) : (root || document).querySelector(selector);
  }

  function readJson(key, fallback) {
    try {
      var value = localStorage.getItem(key);
      return value ? JSON.parse(value) : fallback;
    } catch (error) {
      return fallback;
    }
  }

  function writeJson(key, value) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {}
  }

  function money(cents) {
    return U ? U.money(cents) : (Number(cents || 0) / 100).toFixed(2);
  }

  function intentFromText(text) {
    text = String(text || '').toLowerCase();
    if (/doormat|half.?moon|entry|foyer/.test(text)) return 'entryway';
    if (/runner|hallway|kitchen/.test(text)) return 'runner';
    if (/kantha|throw|quilt|bed/.test(text)) return 'bedroom';
    if (/pillow|cushion/.test(text)) return 'cushion';
    if (/rug|jute|cotton|wool|hemp|carpet|dhurrie/.test(text)) return 'rug';
    return 'room';
  }

  function getCart() {
    if (JH.Cart && typeof JH.Cart.get === 'function') return JH.Cart.get();
    if (window.JHSmartCart && typeof window.JHSmartCart.get === 'function') return window.JHSmartCart.get();
    return Promise.reject(new Error('Cart owner is not available'));
  }

  function cartIntent(cart) {
    return intentFromText(((cart && cart.items) || []).map(function(item) {
      return [item.product_title, item.product_type, item.variant_title, item.handle].join(' ');
    }).join(' '));
  }

  function recentIntent() {
    var recent = readJson('jh_recent_products', []);
    if (recent && recent.length) {
      return recent[0].intent || intentFromText([recent[0].title, recent[0].type, recent[0].tags].join(' '));
    }
    return readJson('jh_last_product_type', 'room') || 'room';
  }

  function recoveryCopy(cart) {
    var hasCart = cart && cart.item_count > 0;
    var intent = hasCart ? cartIntent(cart) : recentIntent();
    var total = cart ? money(cart.total_price) : '';

    if (hasCart && /^(rug|runner|entryway)$/.test(intent)) {
      return {
        title: 'Your handmade rug picks are still waiting',
        text: 'Save your cart or continue checkout before sizes and matching pieces change.',
        offerTitle: 'Cart saved for ' + total,
        offerText: 'Use your unlocked rug offer at checkout, or ask us on WhatsApp for matching pillows, runners and custom size help.'
      };
    }
    if (hasCart && /^(cushion|bedroom)$/.test(intent)) {
      return {
        title: 'Your room styling pieces are ready',
        text: 'Save your cart and we will keep your handmade textiles together for follow-up.',
        offerTitle: 'Complete the look',
        offerText: 'Pair pillows and throws with rugs for a warmer, more finished room.'
      };
    }
    if (hasCart) {
      return {
        title: 'Your cart is ready',
        text: 'Save these handmade pieces before you leave, or continue to secure checkout.',
        offerTitle: 'Cart total ' + total,
        offerText: 'Free shipping worldwide and support for size, color and matching questions.'
      };
    }
    if (/^(rug|runner|entryway)$/.test(intent)) {
      return {
        title: 'Still choosing the right rug?',
        text: 'Save your recent rug finds and get help choosing the right size, shape and matching pieces.',
        offerTitle: 'Need size help?',
        offerText: 'Message us with your room photo or size and we will guide you.'
      };
    }
    return {
      title: 'Still browsing handmade home pieces?',
      text: 'Save your favorites and continue later with relevant rugs, pillows and decor.',
      offerTitle: 'Stay connected',
      offerText: 'We can help you match handmade pieces for your room.'
    };
  }

  function storeCartBehavior(cart) {
    var behavior = readJson('jh_behavior', { views: [], cards: [], collections: [], cart: [] });
    behavior.cart = ((cart && cart.items) || []).map(function(item) {
      return {
        title: item.product_title || item.title || '',
        qty: Number(item.quantity || 1),
        url: item.url || ''
      };
    }).slice(0, 20);
    writeJson('jh_behavior', behavior);
  }

  function personalize(cart) {
    cart = cart || { item_count: 0, total_price: 0, items: [] };
    storeCartBehavior(cart);
    var copy = recoveryCopy(cart);
    var map = [
      ['[data-jh-exit-title]', copy.title],
      ['[data-jh-exit-text]', copy.text],
      ['[data-jh-exit-offer-title]', copy.offerTitle],
      ['[data-jh-exit-offer-text]', copy.offerText]
    ];
    map.forEach(function(pair) {
      var element = qs(pair[0]);
      if (element) element.textContent = pair[1];
    });

    var saveTitle = qs('[data-jh-save-cart-title]');
    var saveText = qs('[data-jh-save-cart-text]');
    if (saveTitle) saveTitle.textContent = cart.item_count > 0 ? 'Save this cart and room plan' : 'Save your favorite handmade finds';
    if (saveText) {
      saveText.textContent = cart.item_count > 0 ?
        'Enter your email and we will save your cart summary, product links, size notes and matching recommendations.' :
        'Enter your email to save recently viewed items and receive relevant size/custom-order help.';
    }
  }

  function saveEngagementState() {
    var data = readJson('jh_engagement_state', { visits: 0, last: '' });
    data.visits = (Number(data.visits) || 0) + 1;
    data.last = window.location.pathname;
    writeJson('jh_engagement_state', data);
  }

  function init() {
    saveEngagementState();
    getCart().then(personalize).catch(function() { personalize(); });
    document.addEventListener('jh:cart:changed', function(event) {
      personalize(event.detail || {});
    });
    document.addEventListener('click', function(event) {
      if (event.target && event.target.closest && event.target.closest('[data-jh-save-cart-open],[data-jh-exit-intent]')) {
        getCart().then(personalize).catch(function() {});
      }
    }, true);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
